'use client'

import Image from 'next/image'
import { content } from '@/config/content'
import { useSiteSettings } from '@/lib/useSiteSettings'

const MusicIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
)

const InstagramIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
)

const ExternalLinkIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

/* ---- CMS-based card (original behavior) ---- */
const SongCard = ({ item }) => {
  const title = item.title || '未命名歌曲'
  const body = item.body || ''
  const imageUrl = item.imageUrl || item.image_url
  const linkUrl = item.linkUrl || item.link_url

  return (
    <div
      className="group overflow-hidden transition-shadow"
      style={{
        backgroundColor: 'var(--color-cardBg)',
        border: '1px solid var(--color-cardBorder)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--effect-cardShadow)',
      }}
    >
      {imageUrl && (
        <div className="relative w-full aspect-square overflow-hidden">
          <Image
            src={imageUrl}
            alt={title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div style={{ padding: '16px 16px 20px' }}>
        <h3
          className="text-sm font-semibold mb-2"
          style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}
        >
          {title}
        </h3>
        {body && (
          <p
            className="text-xs leading-relaxed mb-3"
            style={{ color: 'var(--color-textSecondary)' }}
          >
            {body}
          </p>
        )}
        {linkUrl && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-70"
            style={{ color: 'var(--color-accentAlt)' }}
          >
            <MusicIcon size={14} />
            Listen
          </a>
        )}
      </div>
    </div>
  )
}

/* ---- Instagram embed card ---- */
const IgSongCard = ({ song, index }) => {
  const shortcode = song.igUrl.match(/\/p\/([^/?]+)/)?.[1] || ''
  const embedUrl = `https://www.instagram.com/p/${shortcode}/embed/`

  // noEmbed: 不嵌入 iframe，只顯示純連結卡片
  if (song.noEmbed) {
    return (
      <a
        href={song.igUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group block overflow-hidden transition-all duration-300 hover:scale-[1.02]"
        style={{
          backgroundColor: 'var(--color-cardBg)',
          border: '1px solid var(--color-cardBorder)',
          borderRadius: 'var(--radius-md)',
          textDecoration: 'none',
        }}
      >
        <div
          className="relative w-full flex flex-col items-center justify-center p-6 text-center"
          style={{
            aspectRatio: '4/5',
            background: 'linear-gradient(135deg, var(--color-cardBg) 0%, var(--color-cardBorder) 100%)',
          }}
        >
          <div style={{ color: 'var(--color-accent)', opacity: 0.6 }}>
            <MusicIcon size={36} />
          </div>
          <h3
            className="text-lg font-bold mt-4 leading-tight"
            style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}
          >
            {song.title}
          </h3>
          {song.artist && (
            <p className="text-sm mt-2" style={{ color: 'var(--color-textSecondary)' }}>
              {song.artist}
            </p>
          )}
          <div
            className="mt-4 flex items-center gap-2 px-4 py-2 text-xs font-medium transition-opacity group-hover:opacity-100"
            style={{
              borderRadius: 'var(--radius-full)',
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
              color: '#fff',
              opacity: 0.8,
            }}
          >
            <InstagramIcon size={14} />
            查看作品
          </div>
        </div>
      </a>
    )
  }

  return (
    <div
      className="overflow-hidden"
      style={{
        backgroundColor: 'var(--color-cardBg)',
        border: '1px solid var(--color-cardBorder)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div className="relative w-full" style={{ aspectRatio: '4/5', overflow: 'hidden' }}>
        <iframe
          src={embedUrl}
          className="w-full h-full border-0"
          loading="lazy"
          allowTransparency="true"
          allow="encrypted-media"
          title={`${song.title} — ${song.artist || ''}`}
        />
      </div>
      <a
        href={song.igUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between group"
        style={{ padding: '10px 14px 12px', textDecoration: 'none' }}
      >
        <div className="min-w-0 flex-1">
          <h3
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}
          >
            {song.title}
          </h3>
          {song.artist && (
            <p className="text-xs truncate mt-0.5" style={{ color: 'var(--color-textSecondary)' }}>
              {song.artist}
            </p>
          )}
        </div>
        <div className="opacity-60 group-hover:opacity-100 transition-opacity">
          <ExternalLinkIcon size={12} />
        </div>
      </a>
    </div>
  )
}

export default function SongShowcase({ items = [], fallbackSongs = [] }) {
  const hasCmsItems = items.length > 0
  const showFallback = !hasCmsItems && fallbackSongs.length > 0
  const { settings: brand } = useSiteSettings('brand')
  // Phase 3.9 — song showcase copy from CMS
  const { settings: s } = useSiteSettings('song')
  const brandIgUrl = brand.brand_ig_url || content.brand.igUrl

  /* If neither CMS nor fallback data */
  if (!hasCmsItems && !showFallback) {
    return (
      <section style={{ padding: '0 var(--spacing-pagePadding) 48px' }}>
        <div className="max-w-lg mx-auto text-center py-12">
          <MusicIcon size={32} />
          <p
            className="text-sm mt-4"
            style={{ color: 'var(--color-textMuted)', fontFamily: 'var(--font-body)' }}
          >
            Coming soon...
          </p>
        </div>
      </section>
    )
  }

  /* CMS items exist — use original grid */
  if (hasCmsItems) {
    return (
      <section style={{ padding: '0 var(--spacing-pagePadding) 48px' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <SongCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    )
  }

  /* Fallback: Instagram gallery */
  return (
    <section style={{ padding: '0 var(--spacing-pagePadding) 64px' }}>
      <div className="max-w-6xl mx-auto">
        {/* Intro text */}
        <div className="text-center mb-10" style={{ maxWidth: '560px', margin: '0 auto 40px' }}>
          <p
            className="text-base leading-relaxed mb-2"
            style={{ color: 'var(--color-text)', fontFamily: 'var(--font-body)' }}
          >
            {s.song_section_tagline || '每首歌都有屬於它的畫面，每個圖案都是一段旋律的延伸。'}
          </p>
          <p
            className="text-sm"
            style={{ color: 'var(--color-textSecondary)', fontFamily: 'var(--font-body)' }}
          >
            {s.song_ig_cta_prefix || '點擊作品前往 Instagram 查看完整內容。'}
          </p>
        </div>

        {/* Responsive grid: 2 cols mobile, 3 cols tablet, 4 cols desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {fallbackSongs.map((song, i) => (
            <IgSongCard key={song.id} song={song} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div
          className="text-center mt-14"
          style={{
            padding: '32px 24px',
            backgroundColor: 'var(--color-cardBg)',
            border: '1px solid var(--color-cardBorder)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <p
            className="text-base font-semibold mb-2"
            style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}
          >
            {s.song_custom_heading || '想把你的歌變成刺青？'}
          </p>
          <p
            className="text-sm mb-5"
            style={{ color: 'var(--color-textSecondary)', fontFamily: 'var(--font-body)' }}
          >
            {s.song_custom_body || '歡迎透過 Instagram 或 LINE 與我們聊聊你的想法。'}
          </p>
          <a
            href={brandIgUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
            style={{
              padding: '10px 24px',
              borderRadius: 'var(--radius-md)',
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
              color: '#fff',
              textDecoration: 'none',
            }}
          >
            <InstagramIcon size={16} />
            {s.song_visit_ig_button || '前往 Instagram'}
          </a>
        </div>
      </div>
    </section>
  )
}
